import { Drawer } from "@mui/material";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { Service } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { getCarrierServiceLabel } from "@redotech/redo-fulfillment-carrier-common/carrier-service-labels";
import XIcon from "@redotech/redo-icons/x-close.svg";
import {
  ParcelChanges,
  SurchargeItem,
} from "@redotech/redo-merchant-fulfillment-rpc/schema/surcharge-disputes/get-relevant-surcharge-data";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { IconButton } from "@redotech/redo-web/button";
import { Flex } from "@redotech/redo-web/flex";
import { MoneyText } from "@redotech/redo-web/fx/money-text";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

function Divider() {
  return (
    <Flex
      borderBottomWidth="1px"
      borderColor="secondary"
      borderStyle="solid"
      w="full"
    />
  );
}

function WhatChangedSection({ parcels }: { parcels: ParcelChanges[] }) {
  const parcelsWithChanges = parcels.filter((p) => p.changes.length > 0);
  if (parcelsWithChanges.length === 0) {
    return null;
  }

  return (
    <>
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" textColor="tertiary">
          What changed
        </Text>
        <Flex dir="column" gap="md">
          {parcelsWithChanges.map((parcel, idx) => (
            <Flex dir="column" gap="xxs" key={idx}>
              {parcel.changes.map((change, changeIdx) => (
                <Flex
                  align="center"
                  dir="row"
                  justify="space-between"
                  key={changeIdx}
                  w="full"
                >
                  <Flex dir="column" gap="xxs">
                    <Text fontSize="xs">{change.label}</Text>
                    {parcel.trackingNumber && (
                      <Text fontSize="xxs" textColor="tertiary">
                        {parcel.trackingNumber}
                      </Text>
                    )}
                  </Flex>
                  <Text fontSize="xs">
                    <Text
                      as="span"
                      fontSize="xs"
                      style={{ textDecoration: "line-through" }}
                      textColor="tertiary"
                    >
                      {change.old}
                    </Text>{" "}
                    → {change.new}
                  </Text>
                </Flex>
              ))}
            </Flex>
          ))}
        </Flex>
      </Flex>
      <Divider />
    </>
  );
}

function SurchargeLineItem({
  surcharge,
  onCreateDispute,
}: {
  surcharge: SurchargeItem;
  onCreateDispute: (id: string) => void;
}) {
  const alreadyDisputed = !!surcharge.disputeId;
  return (
    <Flex dir="column" gap="xs">
      <Flex align="center" dir="row" justify="space-between" w="full">
        <Flex dir="column" gap="xxs">
          {surcharge.descriptions.map((description, idx) => (
            <Text fontSize="xs" key={idx}>
              {description}
            </Text>
          ))}
          {surcharge.trackingNumber && (
            <Text fontSize="xxs" textColor="tertiary">
              {surcharge.trackingNumber}
            </Text>
          )}
        </Flex>
        <Text fontSize="xs">
          {/* Surcharges are carrier-billing amounts; the RPC carries no
              currency, so they default to USD (the carrier billing currency). */}
          <MoneyText
            struck
            value={toMoney({ amount: Math.abs(Number(surcharge.amount)) })}
          />
        </Text>
      </Flex>
      <RedoButton
        disabled={alreadyDisputed}
        hierarchy="tertiary"
        onClick={() => onCreateDispute(surcharge.id)}
        size="xs"
        text={alreadyDisputed ? "Dispute submitted" : "Dispute this adjustment"}
        theme="destructive"
      />
    </Flex>
  );
}

export const BalanceTransactionBreakdownSlideout = memo(
  function BalanceTransactionBreakdownSlideout({
    isOpen,
    onClose,
    fulfillmentGroupId,
    onCreateDispute,
  }: {
    isOpen: boolean;
    onClose: () => void;
    fulfillmentGroupId: string;
    onCreateDispute: (balanceTransactionId: string) => void;
  }) {
    const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);

    const surchargeDataResult = useLoad(
      () => rpcClient.getRelevantSurchargeData({ fulfillmentGroupId }),
      [rpcClient, fulfillmentGroupId],
    );

    const surchargeData = surchargeDataResult.value;
    const totalAdjustment = Math.abs(
      Number(surchargeData?.totalAdjustment ?? "0"),
    );

    return (
      <Drawer anchor="right" onClose={onClose} open={isOpen}>
        <Flex dir="column" w="xs">
          <Flex
            borderBottomWidth="1px"
            borderColor="primary"
            borderStyle="solid"
            dir="column"
            gap="none"
          >
            <Flex align="flex-start" dir="row" justify="space-between" p="3xl">
              <Text fontSize="xl" fontWeight="semibold">
                Payment breakdown
              </Text>
              <IconButton aria-label="Close" onClick={onClose}>
                <XIcon />
              </IconButton>
            </Flex>
          </Flex>
          <Flex dir="column" gap="lg" pb="3xl" px="3xl" w="full">
            {surchargeDataResult.pending && (
              <Text fontSize="sm" textColor="secondary">
                Loading...
              </Text>
            )}
            {surchargeDataResult.error && (
              <Text fontSize="sm" textColor="error">
                Error loading data
              </Text>
            )}
            {surchargeData && (
              <>
                <Flex dir="column" gap="sm">
                  <Text fontSize="xs" textColor="tertiary">
                    Base rate
                  </Text>
                  <Flex dir="row" gap="xs" justify="space-between">
                    <Text fontSize="xs">
                      {getCarrierServiceLabel(
                        (surchargeData.carrier as Carriers) ?? null,
                        (surchargeData.serviceLevel as Service) ?? null,
                      ) ??
                        `${surchargeData.carrier ?? ""} ${surchargeData.serviceLevel ?? ""}`.trim()}
                    </Text>
                    <Text fontSize="xs">
                      <MoneyText
                        struck
                        value={toMoney({
                          amount: Math.abs(
                            Number(surchargeData.originalCost ?? "0"),
                          ),
                        })}
                      />
                    </Text>
                  </Flex>
                </Flex>

                {(surchargeData.surcharges.length ?? 0) > 0 && (
                  <Flex
                    bgColor="warning-secondary"
                    borderColor="fg-warning-secondary"
                    borderRadius="lg"
                    borderStyle="solid"
                    borderWidth="1px"
                    dir="column"
                    gap="2xl"
                    p="lg"
                    w="full"
                  >
                    <WhatChangedSection parcels={surchargeData.parcelChanges} />

                    <Flex dir="column" gap="md">
                      {surchargeData.surcharges.map((surcharge, idx) => (
                        <SurchargeLineItem
                          key={idx}
                          onCreateDispute={onCreateDispute}
                          surcharge={surcharge}
                        />
                      ))}
                    </Flex>

                    <Divider />

                    <Flex dir="row" justify="space-between" w="full">
                      <Text fontSize="xs" fontWeight="semibold">
                        Total adjustments
                      </Text>
                      <Text fontSize="xs" fontWeight="semibold">
                        <MoneyText
                          struck
                          value={toMoney({ amount: totalAdjustment })}
                        />
                      </Text>
                    </Flex>
                  </Flex>
                )}

                <Flex dir="column" gap="xxs">
                  <Flex dir="row" justify="space-between" w="full">
                    <Text fontSize="sm" fontWeight="semibold">
                      Updated total
                    </Text>
                    <Text fontSize="sm" fontWeight="semibold">
                      <MoneyText
                        struck
                        value={toMoney({
                          amount: Math.abs(
                            Number(surchargeData.updatedTotal ?? "0"),
                          ),
                        })}
                      />
                    </Text>
                  </Flex>
                  <Text fontSize="xxs" textColor="tertiary">
                    Original{" "}
                    <MoneyText
                      struck
                      value={toMoney({
                        amount: Math.abs(
                          Number(surchargeData.originalCost ?? "0"),
                        ),
                      })}
                    />{" "}
                    + Adjustments{" "}
                    <MoneyText
                      struck
                      value={toMoney({ amount: totalAdjustment })}
                    />
                  </Text>
                </Flex>
              </>
            )}
          </Flex>
        </Flex>
      </Drawer>
    );
  },
);
